import { useState, useEffect, ChangeEvent } from "react";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import _ from "lodash";
import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectUserInfo } from "../userInfo/userInfoSlice";
import { selectActiveTenantId } from "../activeTenant/activeTenantSlice";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  fetchAddressAction,
  selectAddress,
  selectAutoComplete,
  setAutoCompleteValue,
} from "../address/addressSlice";
import {
  selectSolarPanelModellData,
  selectPoolModelData,
  selectRoofModelData,
  selectGraffitiModelData,
  selectWalledWindowModelData,
  selectBuildingAgeModelData,
  selectAIModelSelection,
  setAiModelSelection,
} from "../address/aiDetectionSlice";

import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { arrayMove } from "@dnd-kit/sortable";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Checkbox from "@mui/material/Checkbox";
import AppBar from "../address/AppBar";

import PlacesAutocomplete from "./PlacesAutocomplete";
import SearchBlocksWrapper from "./SearchBlocksWrapper";

import Icon from "../../components/Icon";
import NavigationMenu from "../../components/NavigationMenu";

import { BlockItemType } from "../../types";
import { blockItems } from "../../constants";
import { getLocalStorageItem, setLocalStorageItem } from "../../app/functions";

import "../address/Address.css";
import "./Search.css";
import ButtonConfirm from "../../components/ButtonConfirm/ButtonConfirm";
import { generatePdf } from "../address/data4homeAPI";

const drawerWidth = 350;

const render = (status: Status) => {
  return <div>Loading...</div>;
};

const API_KEY = "AIzaSyDq2gssPa5aiQDHXNsQU7fP9DZd-v5pzho";

export default function ClippedDrawer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const aiDetectionSelectionState = useAppSelector(selectAIModelSelection);
  const { t } = useTranslation();
  const userInfo = useAppSelector(selectUserInfo);
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const googleAutocomplete = useAppSelector(selectAutoComplete);
  const address = useAppSelector(selectAddress);
  const [blockItemList, setBlockItemList] = useState<BlockItemType[]>([]);
  const [isExportDisable, setIsExportDisable] = useState(true);
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);

  const solarPanel = useAppSelector(selectSolarPanelModellData);
  const pool = useAppSelector(selectPoolModelData);
  const roof = useAppSelector(selectRoofModelData);
  const graffiti = useAppSelector(selectGraffitiModelData);
  const walledWindow = useAppSelector(selectWalledWindowModelData);
  const buildingAge = useAppSelector(selectBuildingAgeModelData);

  const [aiModelPermission, setAiModelPermission] = useState({
    pool: false,
    solarPanel: false,
    graffiti: false,
    walledWindow: false,
    buildingAge: false,
  });

  useEffect(() => {
    if (userInfo.value) {
      const pool = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_pool_detection" && item.scope.includes("view")).length > 0;
      const solarPanel = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_solar_panel_detection" && item.scope.includes("view")).length > 0;
      const graffiti = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_graffiti_detection" && item.scope.includes("view")).length > 0;
      const walledWindow = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_walled_window_detection" && item.scope.includes("view")).length > 0;
      const buildingAge = userInfo.value.rtp.filter((item: any) => item.resource === "vision_data4home:ai_building_age_detection" && item.scope.includes("view")).length > 0;

      setAiModelPermission({ pool, solarPanel, graffiti, walledWindow, buildingAge });
    }
  }, [userInfo]);

  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (place: any) => {
    dispatch(setAutoCompleteValue(place));
  };

  const handleSetBlockItemList = (activeId: string, overId: string) => {
    setBlockItemList((items) => {
      const activeIndex = _.findIndex(
        items,
        (item: BlockItemType) => activeId === item.id
      );
      const overIndex = _.findIndex(
        items,
        (item: BlockItemType) => overId === item.id
      );

      const newBlockItemList = arrayMove(items, activeIndex, overIndex);

      setLocalStorageItem(
        "block-result-list",
        JSON.stringify(newBlockItemList)
      );
      return newBlockItemList;
    });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setAiModelSelection(event.target.name, event.target.checked));
  };

  const handleExportPdf = async () => {
    const tenantId = activeTenantId ?? userInfo.value?.tenants?.[0]?.tenantId;
    try {
      setIsLoadingPDF(true);
      const response = await generatePdf(address.value.placeId, undefined, tenantId);
      if (response.status !== 200) {
        enqueueSnackbar("Erreur lors de l'exportation du PDF", {
          variant: "error",
          autoHideDuration: 1000,
        });
      }

      const filePDF = new Blob([response.arraybuffer], {
        type: "application/pdf",
      });
      const fileURLPdf = URL.createObjectURL(filePDF);
      window.open(fileURLPdf, "_blank");
      setIsLoadingPDF(false);
    } catch (error: any) {
      setIsLoadingPDF(false);
      if (error?.response?.status === 403) {
        enqueueSnackbar("Cette adresse est exclue des zones de souscription.", { variant: "error" });
        return;
      }
      enqueueSnackbar("Erreur lors de l'exportation du PDF", {
        variant: "error",
        autoHideDuration: 1000,
      });
      console.error("Erreur lors de l'exportation du PDF", error);
    }
  };

  useEffect(() => {
    const storedBlockItemList = getLocalStorageItem(
      "block-result-list",
      []
    );

    const initialBlockItemList = storedBlockItemList;
    for (let item of blockItems) {
      const userHasAccessRight = !item.accessRight || (userInfo.value && userInfo.value.groups && userInfo.value.groups.includes(item.accessRight));
      const itemAlreadyExists = initialBlockItemList.filter(function(initialItem: any) {
        return item.id === initialItem.id;
      }).length > 0;

      if (userHasAccessRight && !itemAlreadyExists) {
        initialBlockItemList.push(item);
      }
    }

    const iAddr = initialBlockItemList.findIndex((x: any) => x.id === "business_address_at_place");
    const iProx = initialBlockItemList.findIndex((x: any) => x.id === "business_search");
    if (iAddr >= 0 && iProx >= 0 && iAddr > iProx) {
      const [atPlaceBlock] = initialBlockItemList.splice(iAddr, 1);
      initialBlockItemList.splice(iProx, 0, atPlaceBlock);
    }

    setBlockItemList(initialBlockItemList);
  }, [userInfo]);

  useEffect(() => {
    const placeId = searchParams.get('placeId');
    if (placeId) {
      dispatch(fetchAddressAction(placeId));
    }
  }, [searchParams]);

  useEffect(() => {
    if (address.value) {
      document.title = `Vision - ${address.value.address}`;
    } else {
      document.title = 'Vision - Data4Home';
    }
  }, [address]);

  useEffect(() => {
    if (!googleAutocomplete) return;
    const placeId = typeof googleAutocomplete === 'string'
      ? googleAutocomplete
      : googleAutocomplete?.value?.place_id;
    if (!placeId) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('placeId') === placeId) return;
    navigate({ search: `?placeId=${placeId}`, hash: window.location.hash });
  }, [googleAutocomplete, navigate]);

  useEffect(() => {
    if (solarPanel.error) {
      enqueueSnackbar(
        "L'interrogation du modèle de détection de panneaux solaires a échoué",
        { variant: "error" }
      );
    }
  }, [solarPanel, enqueueSnackbar]);

  useEffect(() => {
    if (graffiti.error) {
      enqueueSnackbar(
        "L'interrogation du modèle de détection de graffitis a échoué",
        { variant: "error" }
      );
    }
  }, [graffiti, enqueueSnackbar]);

  useEffect(() => {
    if (walledWindow.error) {
      enqueueSnackbar(
        "L'interrogation du modèle de détection de graffitis a échoué",
        { variant: "error" }
      );
    }
  }, [walledWindow, enqueueSnackbar]);

  useEffect(() => {
    if (pool.error) {
      enqueueSnackbar(
        "L'interrogation du modèle de détection de piscines a échoué",
        { variant: "error" }
      );
    }
  }, [pool, enqueueSnackbar]);

  useEffect(() => {
    if (roof.error) {
      enqueueSnackbar(
        "L'interrogation du modèle de détection de toîts a échoué",
        { variant: "error" }
      );
    }
  }, [roof, enqueueSnackbar]);

  useEffect(() => {
    if (buildingAge.error) {
      enqueueSnackbar(
        "L'interrogation du modèle d'estimation de l'âge du batîment a échoué",
        { variant: "error" }
      );
    }
  }, [buildingAge, enqueueSnackbar]);

  useEffect(() => {
    if (address.error) {
      enqueueSnackbar(
        "L'interrogation du serveur de recherche d'adresse a échoué",
        { variant: "error" }
      );
    }
    if (address.value) {
      setIsExportDisable(false);
    }
  }, [address, enqueueSnackbar]);

  return (
    <Wrapper apiKey={API_KEY} libraries={["places", "marker", "geometry"]} render={render}>
      <Box sx={{ display: "flex", scrollPaddingTop: "50px" }} className="Search">
        <CssBaseline />
        <AppBar
          title={t("applicationMenu.menuItem.data4home")}
          position="fixed"
          sx={{ zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
        />
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
            },
          }}
        >
          <Box
            sx={{ paddingLeft: "12px", paddingRight: "12px", paddingTop: 10 }}
          >
            <PlacesAutocomplete
              initialValue={googleAutocomplete?.label}
              onChange={handleChange}
            />
          </Box>

          <Box
            sx={{ paddingLeft: "12px", paddingRight: "12px", paddingTop: 2, paddingBottom: 1 }}
          >
            <ButtonConfirm
              title="Export PDF"
              onClick={handleExportPdf}
              disabled={isExportDisable}
              loading={isLoadingPDF}
              size="150px"
            />
          </Box>

          <Box>
            <p style={{ paddingLeft: "12px", paddingRight: "12px", marginBottom: "10px"}}>
              Choisissez les algos D4 Home pour lesquels vous souhaitez un
              résultat immédiat lors de la recherche
            </p>
          </Box>

          <Box>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "12px",
              }}
            >
              <Icon containerStyle={{ flex: 1 }} type="pool" />
              <span style={{ flex: 4, color: aiModelPermission.pool ? "black" : "grey" }}>Détection de piscine</span>
              <Checkbox
                style={{ flex: 1 }}
                name="pool"
                onChange={handleCheckboxChange}
                checked={aiDetectionSelectionState["pool"]}
                disabled={pool.status === "loading" || !aiModelPermission.pool}
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "12px",
              }}
            >
              <Icon containerStyle={{ flex: 1 }} type="solarPanel" />
              <span style={{ flex: 4, color: aiModelPermission.solarPanel ? "black" : "grey" }}>Détection de panneaux solaires</span>
              <Checkbox
                style={{ flex: 1 }}
                name="solarPanel"
                onChange={handleCheckboxChange}
                checked={aiDetectionSelectionState["solarPanel"]}
                disabled={solarPanel.status === "loading" || !aiModelPermission.solarPanel}
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "12px",
              }}
            >
              <Icon containerStyle={{ flex: 1 }} type="graffiti" />
              <span style={{ flex: 4, color: aiModelPermission.graffiti ? "black" : "grey" }}>Détection de graffitis</span>
              <Checkbox
                style={{ flex: 1 }}
                name="graffiti"
                onChange={handleCheckboxChange}
                checked={aiDetectionSelectionState["graffiti"]}
                disabled={graffiti.status === "loading" || !aiModelPermission.graffiti}
              />
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "12px",
              }}
            >
              <Icon containerStyle={{ flex: 1 }} type="window" />
              <span style={{ flex: 4, color: aiModelPermission.walledWindow ? "black" : "grey" }}>Détection de fenêtre murées</span>
              <Checkbox
                style={{ flex: 1}}
                name="walledWindow"
                onChange={handleCheckboxChange}
                checked={aiDetectionSelectionState["walledWindow"]}
                disabled={walledWindow.status === "loading" || !aiModelPermission.walledWindow}
              />
            </label>
          </Box>

          <Box style={{ flex: 1 }}>
            <NavigationMenu
              blockItemList={blockItemList}
              handleSetBlockItemList={handleSetBlockItemList}
            />
          </Box>

          <ButtonConfirm
            title="Export PDF"
            onClick={handleExportPdf}
            disabled={isExportDisable}
            loading={isLoadingPDF}
            size="150px"
          />

          <div
            className="BuildNumber"
            style={{ padding: "10px", textAlign: "center" }}
          >
            Version: {process.env.REACT_APP_APP_VERSION} - {process.env.REACT_APP_BUILD_NUMBER}
          </div>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            paddingLeft: 3,
            paddingRight: 3,
            paddingTop: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <SearchBlocksWrapper blockItemList={blockItemList} />
        </Box>
      </Box>
    </Wrapper>
  );
}
